import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang, formatKickoff } from "@/i18n";

const R = 52;
const CIRC = 2 * Math.PI * R;

const Crest = ({ src, label, color }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div
        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center text-lg font-bold tracking-widest font-mono2"
        style={{ background: `${color}14`, border: `1px solid ${color}50`, color }}
      >
        {label}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={label}
      data-testid={`essai-crest-${label.toLowerCase()}`}
      onError={() => setErr(true)}
      className="w-24 h-24 sm:w-28 sm:h-28 object-contain drop-shadow-[0_0_20px_rgba(255,255,255,0.08)]"
    />
  );
};

const Gauge = ({ value }) => (
  <div className="relative w-[120px] h-[120px]">
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <circle cx="60" cy="60" r={R} fill="none" stroke="#1C1C1C" strokeWidth="6" />
      <motion.circle
        cx="60"
        cy="60"
        r={R}
        fill="none"
        stroke="#CCFF00"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={CIRC}
        transform="rotate(-90 60 60)"
        initial={{ strokeDashoffset: CIRC }}
        animate={{ strokeDashoffset: CIRC * (1 - value / 100) }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        style={{ filter: "drop-shadow(0 0 8px rgba(204,255,0,0.5))" }}
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <span data-testid="essai-score" className="font-display font-black text-3xl text-white">{value}</span>
    </div>
  </div>
);

export default function EssaiSection({ today }) {
  const { lang, t } = useLang();
  const e = t.essai;

  const goWaitlist = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Jamais de contenu fictif : la section ne s'affiche qu'avec un vrai match + sa vraie analyse
  if (!today?.match || !today?.meta) return null;
  const { match, meta } = today;
  const rawAnalyse = typeof today?.analyse === "string" ? today.analyse : Array.isArray(today?.analyse) ? today.analyse.join("\n") : "";
  const points = rawAnalyse.split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <section data-testid="essai-section" className="relative py-16 lg:py-20 border-t border-white/5">
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(204,255,0,0.05) 0%, transparent 65%)" }}
      />
      <div className="max-w-3xl mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#CCFF00] mb-4">{e.kicker}</p>
          <h2 className="font-display font-black tracking-tighter leading-none text-4xl sm:text-5xl text-white mb-4">{e.title}</h2>
          <p className="text-base text-zinc-400 leading-relaxed max-w-xl">{e.desc}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="neon-trace bg-[#0A0A0A] p-6 sm:p-10"
        >
          <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-5">
            {match.league} — <span className="text-[#CCFF00]">{formatKickoff(match.kickoff, lang)}</span>
          </p>
          <div className="flex items-start justify-between mb-10 px-2 sm:px-8">
            <div className="flex flex-col items-center gap-3 w-32">
              <Crest src={match.homeCrest} label={match.homeCode} color="#CCFF00" />
              <span className="font-display font-bold text-lg text-white text-center leading-tight">{match.home}</span>
            </div>
            <span className="font-mono2 text-sm text-zinc-600 pt-10">VS</span>
            <div className="flex flex-col items-center gap-3 w-32">
              <Crest src={match.awayCrest} label={match.awayCode} color="#FFC800" />
              <span className="font-display font-bold text-lg text-white text-center leading-tight">{match.away}</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center mb-8 pb-8 border-b border-white/5">
            <div className="flex flex-col items-center gap-2">
              <Gauge value={meta.confiance} />
              <p className="font-mono2 text-[9px] tracking-widest uppercase text-zinc-500">{e.scoreLabel}</p>
              <span className="font-mono2 text-[9px] tracking-widest uppercase px-2 py-1 rounded-sm bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00]">
                {e.confidence}
              </span>
            </div>
            <div>
              <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">{e.verdictLabel}</p>
              <p data-testid="essai-verdict" className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight mb-2">
                {meta.verdict}
              </p>
              <p className="font-display font-black text-4xl text-[#CCFF00] drop-shadow-[0_0_12px_rgba(204,255,0,0.35)]">{meta.probabilite}%</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <div className="rounded-md border border-white/10 bg-[#111111] px-4 py-3">
              <p className="font-mono2 text-[9px] tracking-widest text-zinc-500 uppercase mb-1">{e.oddsLabel}</p>
              <p className="font-mono2 text-sm text-white">{meta.cote}</p>
            </div>
            <div className="rounded-md border border-white/10 bg-[#111111] px-4 py-3">
              <p className="font-mono2 text-[9px] tracking-widest text-zinc-500 uppercase mb-1">{e.likely}</p>
              <p data-testid="essai-score-probable" className="font-display font-black text-lg text-[#FFC800]">{meta.score}</p>
            </div>
          </div>

          {points.length > 0 && (
            <div className="pt-8 border-t border-white/5">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] mb-5">{e.analysisTitle}</p>
              <ul className="space-y-2.5">
                {points.map((p, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="flex items-start gap-2.5 text-sm text-zinc-300"
                  >
                    <Check className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
                    {p}
                  </motion.li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex justify-center"
        >
          <button
            data-testid="essai-cta-button"
            onClick={goWaitlist}
            className="flex items-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-sm rounded-sm px-8 py-4 hover:bg-[#b3e600] shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] transition-colors duration-300"
          >
            {e.cta}
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
